import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ViewMatches() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState(null);
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [unsubscribeReason, setUnsubscribeReason] = useState('');
  const [unsubscribing, setUnsubscribing] = useState(false);
  const [revealedCompanies, setRevealedCompanies] = useState(new Set());
  const [unsubscribed, setUnsubscribed] = useState(false);

  useEffect(() => {
    fetchMatches();
  }, [leadId]);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/view-matches/${leadId}`);
      setLead(response.data.lead);
      setCompanies(response.data.companies);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching matches:', err);
      setError('Unable to load your matches. Please check your link or contact support.');
      setLoading(false);
    }
  };

  const revealCompanyContact = async (companyId) => {
    try {
      // Track the click and notify company
      await axios.post(`${API}/track-lead-click/${leadId}/${companyId}`);
      
      // Add to revealed set
      setRevealedCompanies(prev => new Set([...prev, companyId]));
      
      toast.success('Contact info revealed! Company has been notified.', {
        description: 'They know you viewed their info and will prioritize reaching out.'
      });
    } catch (err) {
      console.error('Error tracking click:', err);
      // Still reveal even if tracking fails
      setRevealedCompanies(prev => new Set([...prev, companyId]));
    }
  };

  const handleCompanyClick = async (companyId) => {
    try {
      // Track the click
      await axios.post(`${API}/track-lead-click/${leadId}/${companyId}`);
      toast.success('Company has been notified of your interest!', {
        description: 'They will prioritize reaching out to you.'
      });
    } catch (err) {
      console.error('Error tracking click:', err);
      // Don't block the user even if tracking fails
    }
  };

  const handleUnsubscribe = async () => {
    if (!unsubscribeReason) {
      toast.error('Please select a reason');
      return;
    }

    setUnsubscribing(true);
    try {
      await axios.post(`${API}/leads/${leadId}/unsubscribe`, {
        reason: unsubscribeReason
      });
      
      toast.success('Successfully unsubscribed', {
        description: 'All matched companies have been notified.'
      });
      
      setShowUnsubscribeModal(false);
      setUnsubscribed(true);
      
    } catch (err) {
      console.error('Error unsubscribing:', err);
      toast.error('Failed to unsubscribe. Please try again or contact support.');
    } finally {
      setUnsubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-red-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading your matches...</p>
        </div>
      </div>
    );
  }

  if (unsubscribed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-amber-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">You've Been Unsubscribed</h2>
          <p className="text-gray-700 mb-6 text-lg font-medium">
            Made a mistake? 
          </p>
          <button
            onClick={() => setUnsubscribed(false)}
            className="bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition shadow-lg"
          >
            Click here to continue to review Top Property Management Companies
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-blue-600 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">
              🎉 Great News!
            </h1>
            <p className="text-lg sm:text-xl text-red-100">
              We found {companies.length} property management {companies.length === 1 ? 'company' : 'companies'} for you
            </p>
            <p className="text-sm sm:text-base text-red-200 mt-2">
              {lead?.city} • {lead?.property_type?.replace('-', ' ').toUpperCase()}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Instructions */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-blue-900 mb-3 flex items-center gap-2">
            <span>💡</span> How This Works
          </h2>
          <ul className="space-y-2 text-blue-800">
            <li className="flex items-start gap-2">
              <span className="font-bold">1.</span>
              <span>Review the companies below - they've all been notified about your property</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">2.</span>
              <span>Click on any company to see their full details and contact information</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">3.</span>
              <span>Compare multiple companies before making your decision</span>
            </li>
          </ul>
        </div>

        {/* Companies Grid */}
        {companies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companies.map((company, index) => (
              <div
                key={company.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-all duration-200 transform hover:scale-105"
              >
                <div className="bg-gradient-to-r from-blue-600 to-blue-600 text-white p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-red-200 mb-1">
                        Company #{index + 1}
                      </div>
                      <h3 className="text-xl font-bold leading-tight">
                        {company.company_name}
                      </h3>
                    </div>
                    <div className="text-3xl ml-2">🏢</div>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  {/* Contact Person */}
                  {company.contact_person && (
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">👤</div>
                      <div>
                        <div className="text-xs text-gray-500 font-semibold uppercase">Contact Person</div>
                        <div className="font-semibold text-gray-900">{company.contact_person}</div>
                      </div>
                    </div>
                  )}

                  {/* Contact Info - Blurred or Revealed */}
                  {!revealedCompanies.has(company.id) ? (
                    <div className="relative">
                      {/* Blurred Preview */}
                      <div className="filter blur-sm select-none pointer-events-none space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">📞</div>
                          <div>
                            <div className="text-xs text-gray-500 font-semibold uppercase">Phone</div>
                            <div className="font-semibold text-blue-600">(555) 123-4567</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">📧</div>
                          <div>
                            <div className="text-xs text-gray-500 font-semibold uppercase">Email</div>
                            <div className="font-semibold text-blue-600">contact@example.com</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">🌐</div>
                          <div>
                            <div className="text-xs text-gray-500 font-semibold uppercase">Website</div>
                            <div className="font-semibold text-blue-600">www.example.com</div>
                          </div>
                        </div>
                      </div>

                      {/* Reveal Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-white/90 backdrop-blur-sm">
                        <button
                          onClick={() => revealCompanyContact(company.id)}
                          className="bg-gradient-to-r from-blue-600 to-blue-600 text-white px-6 py-3 rounded-lg font-bold text-sm hover:from-blue-700 hover:to-blue-700 transition shadow-lg"
                        >
                          🔓 Reveal Contact Info
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Phone - Revealed */}
                      {company.phone && (
                        <div className="flex items-center gap-3 animate-fade-in">
                          <div className="text-2xl">📞</div>
                          <div>
                            <div className="text-xs text-gray-500 font-semibold uppercase">Phone</div>
                            <a 
                              href={`tel:${company.phone}`}
                              className="font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              {company.phone}
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Email - Revealed */}
                      {company.email && (
                        <div className="flex items-center gap-3 animate-fade-in">
                          <div className="text-2xl">📧</div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs text-gray-500 font-semibold uppercase">Email</div>
                            <a 
                              href={`mailto:${company.email}`}
                              className="font-semibold text-blue-600 hover:text-blue-800 hover:underline break-all"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              {company.email}
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Website - Revealed */}
                      {company.website && (
                        <div className="flex items-center gap-3 animate-fade-in">
                          <div className="text-2xl">🌐</div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs text-gray-500 font-semibold uppercase">Website</div>
                            <a 
                              href={company.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-semibold text-blue-600 hover:text-blue-800 hover:underline break-all"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              {company.website}
                            </a>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Address */}
                  {company.address && (
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">📍</div>
                      <div>
                        <div className="text-xs text-gray-500 font-semibold uppercase">Office Address</div>
                        <div className="text-sm text-gray-700">{company.address}</div>
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {company.description && (
                    <div className="pt-4 border-t border-gray-200">
                      <div className="text-xs text-gray-500 font-semibold uppercase mb-2">About</div>
                      <p className="text-sm text-gray-700 line-clamp-3">{company.description}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-8 text-center">
            <div className="text-5xl mb-4">🔍</div>
            <h2 className="text-2xl font-bold text-yellow-900 mb-2">
              We're Finding Your Matches
            </h2>
            <p className="text-yellow-800">
              We're currently searching for property management companies in your area. 
              You'll receive an email update shortly with your matches.
            </p>
          </div>
        )}

        {/* Footer Info */}
        <div className="mt-12 bg-white rounded-lg shadow-lg p-6 border-l-4 border-red-600">
          <h3 className="text-lg font-bold text-gray-900 mb-3">📋 What Happens Next?</h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span>Click on any company below to view their full contact information</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span>All companies have been notified that you're looking for management services</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span>You can expect calls or emails within 24-48 hours</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span>Feel free to reach out to multiple companies to compare services</span>
            </li>
          </ul>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
          <h3 className="text-sm font-bold text-red-900 mb-2">📋 Legal Disclaimer</h3>
          <p className="text-xs text-blue-800 leading-relaxed">
            <strong>Top Prop is not affiliated with any specific property management companies.</strong> 
            We are a for-profit service that connects property owners with property management companies. 
            Top Prop does not endorse, guarantee, or assume any liability for the services provided by 
            the companies we introduce you to. Any agreements, contracts, or arrangements you make with 
            property management companies are solely between you and them. Please conduct your own due 
            diligence before entering into any agreements.
          </p>
        </div>

        {/* Contact Support */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-2">Questions or need help?</p>
          <a 
            href="mailto:info@topprop.ca" 
            className="text-blue-600 hover:text-blue-800 font-semibold hover:underline"
          >
            info@topprop.ca
          </a>
        </div>

        {/* Unsubscribe Button */}
        <div className="mt-8 text-center border-t pt-8">
          <button
            onClick={() => setShowUnsubscribeModal(true)}
            className="text-gray-500 hover:text-gray-700 text-sm underline"
          >
            No longer need property management? Unsubscribe
          </button>
        </div>
      </div>

      {/* Unsubscribe Modal */}
      {showUnsubscribeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Unsubscribe</h2>
            <p className="text-gray-600 mb-6">
              We're sorry to see you go! Please let us know why you're unsubscribing:
            </p>

            <div className="space-y-3 mb-6">
              <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition">
                <input
                  type="radio"
                  name="unsubscribe-reason"
                  value="no-longer-interested"
                  checked={unsubscribeReason === 'no-longer-interested'}
                  onChange={(e) => setUnsubscribeReason(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <div className="font-semibold text-gray-900">I am no longer interested</div>
                  <div className="text-sm text-gray-500">I've decided not to pursue property management at this time</div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition">
                <input
                  type="radio"
                  name="unsubscribe-reason"
                  value="found-manager"
                  checked={unsubscribeReason === 'found-manager'}
                  onChange={(e) => setUnsubscribeReason(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <div className="font-semibold text-gray-900">I found/selected my new property manager</div>
                  <div className="text-sm text-gray-500">I've chosen a property management company</div>
                </div>
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowUnsubscribeModal(false);
                  setUnsubscribeReason('');
                }}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition"
                disabled={unsubscribing}
              >
                Cancel
              </button>
              <button
                onClick={handleUnsubscribe}
                disabled={!unsubscribeReason || unsubscribing}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {unsubscribing ? 'Unsubscribing...' : 'Unsubscribe'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
